import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomers, toggleCustomerStatus } from "../../../slice/customer/customerslice";

/* ── Avatar colour from name initial ── */
const avatarColor = (name) => {
  const colors = ["#6362e7", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"];
  if (!name) return colors[0];
  return colors[name.charCodeAt(0) % colors.length];
};

const ListCustomer = () => {
  const dispatch = useDispatch();
  const { customers, loading, error, pagination } = useSelector((state) => state.customer);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  /* ── Toggle confirm modal ── */
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchCustomers({ page, limit, search: searchTerm }));
  }, [dispatch, page, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const formatLastLogin = (dateStr) => {
    if (!dateStr) return "Never";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return formatDate(dateStr);
  };

  /* ─────── TOGGLE ─────── */
  const handleToggleClick = (item) => {
    setToggleTarget(item);
    setShowToggleModal(true);
  };

  const handleToggleAccept = () => {
    if (!toggleTarget) return;
    dispatch(toggleCustomerStatus(toggleTarget._id));
    setShowToggleModal(false);
    setToggleTarget(null);
  };

  const handleToggleCancel = () => {
    setShowToggleModal(false);
    setToggleTarget(null);
  };

  const willBlock = toggleTarget?.status === true;

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: "#111827" }}>Customer</h1>
          <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: 14 }}>
            {pagination.total ?? customers.length} customers registered
          </p>
        </div>
      </div>

      {/* ── Search ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 10,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px",
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#9aa0ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            style={{ border: "none", outline: "none", flex: 1, fontSize: 14, background: "transparent" }}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #eef0f4", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>{error}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfc" }}>
                  {["Si No", "CUSTOMER", "PHONE", "EMAIL", "STATUS", "LAST LOGIN", "JOINED DATE"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left", padding: "14px 20px", fontSize: 11,
                        fontWeight: 700, letterSpacing: 0.5, color: "#9ca3af",
                        borderBottom: "1px solid #eef0f4", whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "50px 0", color: "#aaa" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#bbb" }}>No customers found</span>
                        <span style={{ fontSize: 12, color: "#d1d5db" }}>
                          {searchTerm ? `No results for "${searchTerm}"` : "Customers who register via the app will appear here"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  customers.map((item, index) => (
                    <tr key={item._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td style={{ padding: "16px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 38, height: 38, borderRadius: 10,
                            background: avatarColor(item.name),
                            color: "#fff", fontWeight: 700, fontSize: 14,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            {item.name ? item.name.charAt(0).toUpperCase() : "?"}
                          </div>
                          <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
                            {item.name || <span style={{ color: "#aaa", fontStyle: "italic" }}>No name</span>}
                          </span>
                        </div>
                      </td>

                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563", fontFamily: "monospace" }}>
                        {item.phoneNumber || "-"}
                      </td>

                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {item.email || <span style={{ color: "#ccc" }}>-</span>}
                      </td>

                      {/* ── Toggle → opens confirm modal ── */}
                      <td style={{ padding: "16px 20px" }}>
                        <label className="switch" style={{ cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={item.status}
                            onChange={() => handleToggleClick(item)}
                          />
                          <span className="slider round" />
                        </label>
                      </td>

                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {formatLastLogin(item.last_login)}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {formatDate(item.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "16px 20px" }}>
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer" }}
            >Prev</button>
            {Array.from({ length: pagination.totalPages }, (_, i) => (
              <button key={i + 1} onClick={() => setPage(i + 1)}
                style={{
                  padding: "7px 14px", borderRadius: 8,
                  border: page === i + 1 ? "none" : "1px solid #e5e7eb",
                  background: page === i + 1 ? "#111827" : "#fff",
                  color: page === i + 1 ? "#fff" : "#374151",
                  cursor: "pointer",
                }}
              >{i + 1}</button>
            ))}
            <button disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === pagination.totalPages ? "not-allowed" : "pointer" }}
            >Next</button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          TOGGLE CONFIRM MODAL
      ══════════════════════════════════════════ */}
      {showToggleModal && (
        <>
          <div
            onClick={handleToggleCancel}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1050, width: "100%", maxWidth: 460,
            background: "#fff", borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            padding: "36px 32px 32px",
          }}>
            {/* × close */}
            <button
              onClick={handleToggleCancel}
              style={{
                position: "absolute", top: 16, right: 16,
                width: 32, height: 32, borderRadius: "50%",
                border: "1.5px solid #e5e7eb", background: "#f9fafb",
                cursor: "pointer", fontSize: 18, color: "#555",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              ×
            </button>

            {/* Title */}
            <h2 style={{ margin: "0 0 14px", fontSize: 22, fontWeight: 700, color: "#111" }}>
              Confirm Action
            </h2>

            {/* Message */}
            <p style={{ margin: "0 0 8px", fontSize: 15, color: "#444", lineHeight: 1.6 }}>
              Are you sure you want to{" "}
              <span style={{ color: willBlock ? "#ef4444" : "#16a34a", fontWeight: 700 }}>
                {willBlock ? "Block" : "Activate"}
              </span>{" "}
              this customer?
            </p>

            {/* Customer hint */}
            {toggleTarget?.name && (
              <p style={{ margin: "0 0 28px", fontSize: 13, color: "#888" }}>
                Customer:{" "}
                <span style={{ fontWeight: 700, color: "#333" }}>{toggleTarget.name}</span>
                {" · "}
                <span style={{ fontFamily: "monospace" }}>{toggleTarget.phoneNumber}</span>
              </p>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
              <button
                onClick={handleToggleCancel}
                style={{
                  flex: 1, padding: "13px 0", borderRadius: 10,
                  border: "1.5px solid #e5e7eb", background: "#f9fafb",
                  color: "#555", fontWeight: 700, fontSize: 15,
                  cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 8,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Cancel
              </button>

              <button
                onClick={handleToggleAccept}
                style={{
                  flex: 1, padding: "13px 0", borderRadius: 10,
                  border: "none", background: willBlock ? "#ef4444" : "#16a34a",
                  color: "#fff", fontWeight: 700, fontSize: 15,
                  cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 8,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {willBlock ? "Yes, Block" : "Yes, Activate"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListCustomer;